import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { LocalStorage } from '@vectord/utils';

export interface Attribute {
  id: string;
  attributeName: string;
  schemaName: string;
  checked: boolean;
}

export interface SelectLeadAttributesModalState {
  attributes: Record<string, Attribute>;
  searchText: string;
  checkedIds: string[];
}

const initialState: SelectLeadAttributesModalState = {
  attributes: {},
  searchText: '',
  checkedIds: [],
};

const lsInstance = LocalStorage.getInstance('store.leads.export');

const savedConfig: {
  checkedIds: string[]
} = lsInstance?.config || {};

export const exportLeadsSlice = createSlice({
  name: 'leads/exportLeads',
  initialState: {
    ...initialState,
    ...savedConfig,
  },
  reducers: {
    setExportAttributes: (state, action: PayloadAction<Attribute[]>) => {
      const { checkedIds } = state;
      const attributes = action.payload.reduce((acc, attribute) => {
        acc[attribute.id] = attribute;
        acc[attribute.id]!.checked = attribute.checked || checkedIds.includes(attribute.id);
        return acc;
      }, {} as Record<string, Attribute>);
      state.attributes = attributes;
    },
    toggleExportAttributeChecked: (state, action: PayloadAction<Attribute['id']>) => {
      const attribute = state.attributes[action.payload];
      if (attribute) {
        attribute.checked = !attribute.checked;
        if (attribute.checked) {
          if (!state.checkedIds.includes(action.payload)) {
            state.checkedIds.push(action.payload); // Add to checked IDs
          }
        } else {
          state.checkedIds = state.checkedIds.filter((id) => id !== action.payload); // Remove from checked IDs
        }
      }
    },
    setExportSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    resetExportLeads: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('leads/exportLeads/'),
      (state) => {
        // Save only checked ids to local storage
        lsInstance.config = { checkedIds: state.checkedIds };
      },
    );
  },
});

export const {
  setExportAttributes,
  toggleExportAttributeChecked,
  setExportSearchText,
  resetExportLeads,
} = exportLeadsSlice.actions;

export default exportLeadsSlice.reducer;
